import clone from 'clone';
import * as React from 'react';
import { fieldValidation, LinkReference, ReferenceLink } from '../../amorg-common';
import { getReferenceLinks, setReferenceLinks } from '../../api/referenceLinkClientApi';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { TextField } from '../../designSystem/formField/text/TextField';
import { DarkLink } from '../../designSystem/Link';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../designSystem/Message';
import { Modal } from '../../designSystem/Modal';
import { Small, Span14 } from '../../designSystem/typography/Body';
import { Button } from '../button/Button';
import { Card } from '../Card';
import { CollapsiblePanelHeader } from '../collapsible/CollapsiblePanelHeader';
import { colors } from '../colors/color';
import { Label } from '../Label';
import { ListItem } from '../List/ListItem';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { CountAvatar } from '../avatar/CountAvatar';

type ReferenceLinksPanelCollapsibleProps = {
    reference:LinkReference,
    hide?:boolean,
    onGetLinksCount?: (count:number) => void
}

export function ReferenceLinksPanelCollapsible(props:ReferenceLinksPanelCollapsibleProps):JSX.Element
{
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [linksCount, setLinksCount] = React.useState<number>(0);

    if(props.hide)
        return <></>;

    return (
        <Flex flexDirection="column" gap={4}>
            <CollapsiblePanelHeader
                isOpen={isOpen}
                onChangeOpenState={setIsOpen}
                closedOrDefaultHeader={
                    <Flex gap={8} alignItems='center'>
                        <Label>Links</Label>
                        <CountAvatar size="20px" count={linksCount} />
                    </Flex>
                }
            />
            
            <Card variant="outlined" style={{minWidth:0}}>
                <ReferenceLinksPanel 
                    reference={props.reference} 
                    hide={props.hide} 
                    onGetLinksCount={(count) =>
                        {
                            setIsOpen(count > 0);
                            setLinksCount(count);
                            props.onGetLinksCount?.(count);
                        }
                    }
                />
            </Card>
        </Flex>
    );
}


type ReferenceLinksPanelProps = 
{
    reference:LinkReference,
    hide?:boolean,
    onGetLinksCount?: (count:number) => void
}

export function ReferenceLinksPanel(props:ReferenceLinksPanelProps):JSX.Element
{
    const [loading, setLoading] = React.useState<boolean>(false);
    const [links, setLinks] = React.useState<ReferenceLink[]>([]);

    React.useEffect(() => {
        const getData = async () => {
            setLoading(true);
            const data = await getReferenceLinks(props.reference.id, props.reference.type);
            if(data !== null)
                setLinks(data.links);
            props.onGetLinksCount?.(data?.links.length || 0);
            setLoading(false);
        }
        getData();
    }, [props.reference.id, props.reference.type]);

    async function handleUpdate(links:ReferenceLink[]): Promise<{ errorMessage?:string }>
    {
        setLoading(true);
        const result = await setReferenceLinks(props.reference, links);
        console.log(result);
        if(!result.isSuccess){
            setLoading(false);
            return { errorMessage:'Der opstod en uventet fejl. Prøv igen eller kontakt support' };
        }
        else {
            const data = await getReferenceLinks(props.reference.id, props.reference.type);
            setLinks(data === null ? [] : data.links);
            props.onGetLinksCount?.(data?.links.length || 0);
            setLoading(false);
            return { };
        }
    }
 
    if(props.hide)
        return <></>;
    
    return (
        <>
            <LoadingPageOverlay loading={loading} />
        
            <BaseReferenceLinksPanel 
                links={links} 
                onUpdate={handleUpdate} 
            />
        </>
    );
}


type BaseReferenceLinksPanelProps = 
{
    links:ReferenceLink[],
    onUpdate:(links:ReferenceLink[])=>Promise<{ errorMessage?:string }>
}

function BaseReferenceLinksPanel(props:BaseReferenceLinksPanelProps):JSX.Element
{
    const [errorMessage, setErrorMessage] = React.useState<string>('');

    const [inEditLink, setInEditLink] = React.useState<{
        link:ReferenceLink,
        linkIndex:number,
        hideValidation:boolean,
        errorMessage?:string
    } | null>(null);

    const [inCreationLink, setInCreationLink] = React.useState<{
        link:ReferenceLink,
        hideValidation:boolean,
        errorMessage?:string
    } | null>(null);

    const [deleteLinkIndex, setDeleteLinkIndex] = React.useState<number | null>(null);

    async function handleConfirmDelete()
    {
        if(deleteLinkIndex === null)
            return;

        setErrorMessage('');

        const updatedCollection = clone(props.links);
        updatedCollection.splice(deleteLinkIndex, 1);

        const result = await props.onUpdate(updatedCollection);
        if(result.errorMessage)
        {
            setErrorMessage(result.errorMessage);
        }
    }
    
    return (
        <Flex flexDirection="column" justifyContent='stretch' grow='1' gap={4} style={{flexGrow: 1, minWidth:0}}>
            
            <Flex flexDirection="column" gap={4} style={{minWidth:0}} >
                {props.links.map((link, index) => {
    

                    const a = 
                        <DarkLink
                            style={{
                                fontSize:'14px',
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap'
                            }}
                            href={
                                (link.url.startsWith('http://') || link.url.startsWith('https://') )
                                    ? link.url
                                    : `https://${link.url}`} 
                                target="_blank">
                                {link.url}
                        </DarkLink>
                    
                    return (

                        <ListItem key={index}
                            border
                            padding="6px"
                            leadingIcon='interface_actions_externalLink'
                            leadingIconColor={colors.icon.active}
                            leadingIconBorder={true}
                            primaryContent={
                                <Flex flexDirection="column">
                                    <Span14 fontWeight={400} color="gray">{link.title}</Span14>
                                    <Small>{a}</Small>
                                </Flex>
                            }
                            buttons={[
                                { type:'button', icon: 'interface_actions_delete', onClick: () => setDeleteLinkIndex(index) },
                                { type:'button', icon: 'interface_actions_edit', onClick: () => setInEditLink({
                                    link:link,
                                    linkIndex:index,
                                    hideValidation:true
                                })}
                            ]} 
                        />

                    );
                })}

                {props.links.length === 0 && (
                    <Small>Ingen links oprettet</Small>
                )}

                {errorMessage &&
                    <Flex flexDirection="row" justifyContent="center" alignItems="center">
                        <MessageCard variant='error' grow={1}>{errorMessage}</MessageCard>
                    </Flex>
                }
            </Flex>

            <Flex justifyContent="flex-end">
                <Button variant='outlined' size='32px' text='Tilføj link' icon="interface_actions_link"
                    onClick={() => setInCreationLink({
                        link:{ title:'', description:'', url:'https://', iconName:null },
                        hideValidation:true
                    })} />
            </Flex>

            <DeleteConfirmationModal
                showModal={deleteLinkIndex !== null}
                requireKey={false}
                onCancel={() => setDeleteLinkIndex(null)}
                buttonText='Slet link'
                confirmationHeading='Slet link'
                confirmationText='Er du sikker på at du vil slette linket?'
                onConfirmDelete={handleConfirmDelete}
            />
            
            <LinkEditorModal
                link={inEditLink?.link}
                hideValidation={inEditLink?.hideValidation || false}
                errorMessage={inEditLink ? inEditLink.errorMessage : undefined}
                isCreate={false}
                onCancel={() => setInEditLink(null)}
                onChange={(updatedLink) => !inEditLink 
                    ? undefined 
                    : setInEditLink({...inEditLink, link:updatedLink})}
                onSubmit={async (valid:boolean) => {
                    if(!inEditLink) 
                        return;

                    setErrorMessage('');

                    if(!valid)
                    {
                        setInEditLink({...inEditLink, hideValidation:false});
                        return;
                    }
                    
                    const updatedCollection = clone(props.links);
                    updatedCollection[inEditLink.linkIndex] = inEditLink.link;

                    const result = await props.onUpdate(updatedCollection);
                    if(result.errorMessage)
                    {
                        setInEditLink({
                            ...inEditLink,
                            hideValidation:true,
                            errorMessage:result.errorMessage
                        });
                    }
                    else {
                        setInEditLink(null);
                    }
                }}
            />

            <LinkEditorModal
                link={inCreationLink?.link}
                hideValidation={inCreationLink?.hideValidation || false}
                errorMessage={inCreationLink ? inCreationLink.errorMessage : undefined}
                isCreate
                onCancel={() => setInCreationLink(null)}
                onChange={(updatedLink) => !inCreationLink 
                    ? undefined 
                    : setInCreationLink({...inCreationLink, link:updatedLink})}
                onSubmit={async (valid:boolean) => {
                    if(!inCreationLink) 
                        return;

                    setErrorMessage('');

                    if(!valid)
                    {
                        setInCreationLink({...inCreationLink, hideValidation:false});
                        return;
                    }

                    const updatedCollection = clone(props.links);
                    updatedCollection.push(inCreationLink.link);

                    const result = await props.onUpdate(updatedCollection);
                    if(result.errorMessage)
                    {
                        setInCreationLink({
                            ...inCreationLink,
                            hideValidation:false,
                            errorMessage:result.errorMessage
                        });
                    }
                    else {
                        setInCreationLink(null);
                    }
                }}
            />
        </Flex>
    );
}


type LinkEditorModalProps = {
    link?:ReferenceLink,
    hideValidation:boolean,
    errorMessage?:string,
    isCreate?:boolean,
    onChange:(referenceLink:ReferenceLink)=>void,
    onSubmit:(valid:boolean)=>void,
    onCancel:()=>void
}

function LinkEditorModal(props:LinkEditorModalProps):JSX.Element
{
    return (
        <>
            {props.link &&
                <Modal
                    title={props.isCreate ? "Opret link" : "Opdater link"}
                    showModal={true}
                    onCancel={props.onCancel}
                    content={
                        <Flex flexDirection='column' gap={16}>
                            {props.errorMessage &&
                                <MessageCard variant='error'>{props.errorMessage}</MessageCard>
                            }

                            <TextField
                                label="Titel"
                                value={props.link.title}
                                onChange={(value) => { props.link ? props.onChange({ ...props.link, title:value }) : undefined }}
                                errorMessage={
                                    props.hideValidation || fieldValidation.referenceLink.title(props.link.title)
                                        ? ''
                                        : `Titel skal udfyldes. Må maks bestå af 100 tegn` 
                                }
                            />

                            {/* <TextAreaField
                                label="Beskrivelse"
                                value={props.link.description}
                                onChange={(value) => { props.link ? props.onChange({ ...props.link, description:value }) : undefined }}
                                errorMessage={
                                    props.hideValidation || fieldValidation.referenceLink.description(props.link.description)
                                        ? ''
                                        : `Beskrivelse skal udfyldes. Må maks bestå af 1000 tegn` 
                                }
                            /> */}


                            <TextField
                                label="url"
                                value={props.link.url}
                                onChange={(value) => { props.link ? props.onChange({ ...props.link, url:value }) : undefined }}
                                errorMessage={
                                    props.hideValidation || fieldValidation.referenceLink.url(props.link.url)
                                        ? ''
                                        : `URL skal udfyldes med en gyldig webaddresse der starter med https:// og er maks 500 tegn (${props.link.url.length})` 
                                }
                            />

                            <Button variant='filled' text={props.isCreate ? "Opret link" : "Opdater link"} 
                                onClick={() => {
                                    props.onSubmit(
                                        fieldValidation.referenceLink.title(props.link?.title || '') &&
                                        fieldValidation.referenceLink.description(props.link?.description || '') &&
                                        fieldValidation.referenceLink.url(props.link?.url || '')
                                    );
                                }
                            } />
                        </Flex>
                    }
                />
            }
        </>
    );
}